import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addAreaCampaign,
  deleteAreaCampaign,
  getCampaignArea,
  getCampaignDetailItems,
  updateCampaign,
  updateCampaignDetailItem,
} from "@/sideEffects/api";
import { CampaignDetails, CampaignType } from "@/lib/types";
import { FormSchemaType, CampaignMetaForm } from "../form/CampaignMetaForm";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { formatDateToLocal, formatToUTC } from "@/lib/dateUtils";
import { addDays } from "date-fns";
import { LoadingSmall } from "@/components/LoadingSmall";
import _ from "lodash";

interface Props {
  campaignDetails: CampaignDetails;
}

export const CampaignUpdateMetaForm: React.FC<Props> = ({ campaignDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const { data: campaignDetailItems, isLoading: isCampaignDetailItemsLoading } = useQuery({
    queryKey: ["getCampaignDetailItems", campaignDetails.intId],
    queryFn: () => getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid)),
  });

  const campaignDetailItemsData = campaignDetailItems?.data.result || [];

  const { data: campaignAreas, isLoading: isCampaignAreasLoading } = useQuery({
    queryKey: ["getCampaignArea", campaignDetails.intId],
    queryFn: () => getCampaignArea(campaignDetails.id, String(activeFirm?.firmGuid)),
  });

  const campaignAreasData = campaignAreas?.data.result || [];

  const queryClient = useQueryClient();
  const updateCampaignMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      // campaign detail
      if (values.periodEnd === values.periodStart) {
        values.periodEnd = addDays(formatToUTC(values.periodEnd), 1).toISOString();
      } else {
        values.periodEnd = formatToUTC(values.periodEnd);
      }

      const campaignDetailItems = await getCampaignDetailItems(campaignDetails.id, String(activeFirm?.firmGuid));
      const campaignDetailItemsData = campaignDetailItems?.data.result || [];
      const campaignDetailItem = campaignDetailItemsData[0];

      await updateCampaignDetailItem({
        campaignDetailItemId: campaignDetailItem.id,
        campaignId: Number(campaignDetailItem.campaignintId),
        periodStart: formatToUTC(values.periodStart),
        periodEnd: values.periodEnd,
        isActive: true,
        firmId: String(activeFirm?.firmGuid),
        withCustomWeekDays: campaignDetailItem.withCustomWeekDays,
      });

      for (const area of campaignAreasData) {
        await deleteAreaCampaign(area.id, String(activeFirm?.firmGuid));
      }

      if (values.areas?.length) {
        for (const areaId of _.uniq(values.areas)) {
          await addAreaCampaign({
            firmGuid: String(activeFirm?.firmGuid),
            campaignId: campaignDetails.intId,
            areaId,
          });
        }
      }

      return updateCampaign({
        id: campaignDetails.id,
        name: values.name,
        isCombineAndShuffle: values.isCombineAndShuffle,
        defaultContentId: values.contentId || undefined,
        firmGuid: String(activeFirm?.firmGuid),
        campaignType: CampaignType.Standard,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCampaign"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCampaignDetails"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCampaignDetailItems"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCampaignArea"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (isCampaignDetailItemsLoading || isCampaignAreasLoading) return <LoadingSmall />;

  return (
    <CampaignMetaForm
      buttonText={t("common.update")}
      onSubmit={(values) => updateCampaignMutation.mutate(values)}
      isLoading={updateCampaignMutation.isPending}
      defaultValues={{
        name: campaignDetails.name,
        isCombineAndShuffle: campaignDetails.isCombineAndShuffle,
        contentId: campaignDetails.defaultContentintId,
        type: campaignDetails.campaignType.toString(),
        periodStart: campaignDetailItemsData[0].periodStart
          ? formatDateToLocal(campaignDetailItemsData[0].periodStart, true)
          : "",
        periodEnd: campaignDetailItemsData[0].periodEnd
          ? formatDateToLocal(campaignDetailItemsData[0].periodEnd, true)
          : "",
        areas: campaignAreasData.map((area) => area.areaintId),
      }}
      showTypeSelect={false}
    />
  );
};
